/*
 * Fleece-lining add-on (snippets/lining-addon.liquid).
 * Ticking or unticking swaps the main product section to the product's lining twin, same colour,
 * and keeps the lock / sold-out state in step with the selected colour. A colour stays clickable
 * while either twin has it in stock; picking one this side is out of swaps to the twin.
 * Other sections marked [data-lining-swap] (Defender pages: details, feature map, review quotes) are re-rendered
 * with the twin in the same swap (Section Rendering API, up to 5 per request), so the whole page follows the lining.
 * A ?view= on the page URL is carried into every request, so alternate-template previews swap correctly.
 */
(() => {
  if (window.SJLiningAddon) return;
  window.SJLiningAddon = true;

  const UNAVAILABLE_CLASS = "product-option--unavailable";
  const SOLD_OUT_CLASS = "product-option--sold-out";
  let lastAutoSwap = 0;

  const readData = root => {
    try {
      return JSON.parse(root.querySelector(".js-lining-data").textContent);
    } catch (error) {
      return null;
    }
  };

  const sectionOf = root => document.getElementById(`shopify-section-${root.dataset.section}`);

  const currentTitle = (root, data) => {
    const idInput = sectionOf(root)?.querySelector('.js-product-form input[name="id"]');
    const id = Number(idInput?.value);
    return Object.keys(data.variants).find(title => data.variants[title].self.id === id);
  };

  // The theme greys a colour this product is out of; keep it live when the twin has it.
  const ungreyColours = (root, data) => {
    const section = sectionOf(root);
    if (!section) return;
    Object.entries(data.variants).forEach(([title, variant]) => {
      if (variant.self.available || !variant.pair?.available) return;
      section.querySelectorAll(`.js-variant-option-value[data-value="${CSS.escape(title)}-0"]`).forEach(option => {
        option.classList.remove(UNAVAILABLE_CLASS, SOLD_OUT_CLASS);
      });
    });
  };

  // Show the twin's own review stars; a twin with no reviews of its own shows none (never this product's stars).
  const keepReviewBadge = section => {
    const badge = section.querySelector(".jdgm-preview-badge");
    if (!badge) return;
    const count = Number(badge.querySelector("[data-number-of-reviews]")?.dataset.numberOfReviews || 0);
    if (count) {
      badge.classList.add("jdgm--done-setup");
      badge.querySelectorAll(".jdgm-prev-badge").forEach(element => { element.style.display = ""; });
    } else {
      badge.style.display = "none";
    }
  };

  // The page's other sections that follow the lining (marked [data-lining-swap]), by section ID.
  const swapSectionIds = mainId => {
    const ids = [];
    document.querySelectorAll("[data-lining-swap]").forEach(element => {
      const id = element.closest(".shopify-section")?.id.replace(/^shopify-section-/, "");
      if (id && id !== mainId && !ids.includes(id)) ids.push(id);
    });
    return ids;
  };

  const fetchSections = async (url, ids) => {
    const markup = {};
    for (let i = 0; i < ids.length; i += 5) {
      const chunk = ids.slice(i, i + 5);
      const response = await fetch(`${url}&sections=${chunk.map(encodeURIComponent).join(",")}`);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const json = await response.json();
      chunk.forEach(id => {
        const fresh = json[id] && new DOMParser().parseFromString(json[id], "text/html").getElementById(`shopify-section-${id}`);
        if (!fresh) throw new Error(`twin section ${id} missing`);
        markup[id] = fresh.innerHTML;
      });
    }
    return markup;
  };

  const swap = async (root, data, title) => {
    const target = data.variants[title]?.pair;
    if (!target) return;
    const section = sectionOf(root);
    const view = new URLSearchParams(window.location.search).get("view");
    const url = `${data.pairUrl}?variant=${target.id}${view ? `&view=${encodeURIComponent(view)}` : ""}`;
    const extraIds = swapSectionIds(data.section);
    root.classList.add("is-loading");
    try {
      const [response, extra] = await Promise.all([
        fetch(`${url}&section_id=${encodeURIComponent(data.section)}`),
        extraIds.length ? fetchSections(url, extraIds) : null
      ]);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const markup = new DOMParser().parseFromString(await response.text(), "text/html");
      const fresh = markup.getElementById(`shopify-section-${data.section}`);
      if (!section || !fresh?.querySelector(".js-lining-addon")) throw new Error("twin section missing");
      const quantity = section.querySelector(".js-product-quantity")?.value;
      section.innerHTML = fresh.innerHTML;
      keepReviewBadge(section);
      if (quantity) section.querySelectorAll(".js-product-quantity").forEach(input => { input.value = quantity; });
      const swapped = [section];
      Object.entries(extra || {}).forEach(([id, html]) => {
        const other = document.getElementById(`shopify-section-${id}`);
        if (!other) return;
        other.innerHTML = html;
        swapped.push(other);
      });
      window.history.replaceState({}, "", url);
      // The theme fades each .js-animate block in once, on scroll (theme.js adds "animated"); swapped-in markup would stay
      // at opacity 0, a white page on phones, so show it straight away.
      swapped.forEach(element => element.querySelectorAll(".js-animate").forEach(node => node.classList.add("animated")));
      // Defender page sections (gallery notes, tabs, feature map) set themselves up again on the new markup.
      swapped.forEach(element => window.D26PDP?.init?.(element));
      init();
    } catch (error) {
      window.location.assign(url);
    }
  };

  const render = (root, data, title) => {
    const variant = data.variants[title];
    if (!variant) return;
    const selfOk = variant.self.available;
    const pairOk = Boolean(variant.pair?.available);

    if (!selfOk && pairOk && Date.now() - lastAutoSwap > 3000) {
      lastAutoSwap = Date.now();
      swap(root, data, title);
      return;
    }

    const fleeceOk = data.on ? selfOk : pairOk;
    const unlinedOk = data.on ? pairOk : selfOk;
    const locked = !(selfOk && pairOk);
    let note = data.on ? data.text.on : data.text.off;
    if (fleeceOk && !unlinedOk) note = data.text.onOnly;
    if (unlinedOk && !fleeceOk) note = data.text.offOnly;

    const input = root.querySelector(".js-lining-input");
    input.checked = data.on;
    input.disabled = locked;
    root.querySelector(".sj-lining").classList.toggle("is-on", data.on);
    root.querySelector(".sj-lining").classList.toggle("is-locked", locked);
    root.querySelector(".js-lining-tag").hidden = fleeceOk;
    root.querySelector(".js-lining-delta").textContent = variant.delta || "";
    root.querySelector(".js-lining-note").textContent = note;
  };

  const refresh = root => {
    const data = readData(root);
    if (!data) return;
    const title = currentTitle(root, data);
    if (title) render(root, data, title);
    ungreyColours(root, data);
  };

  const init = () => {
    document.querySelectorAll(".js-lining-addon").forEach(root => setTimeout(() => refresh(root), 0));
  };

  document.addEventListener("change", event => {
    const target = event.target;
    if (target.matches?.(".js-lining-input")) {
      const root = target.closest(".js-lining-addon");
      const data = readData(root);
      const title = data && currentTitle(root, data);
      if (title) swap(root, data, title);
      return;
    }
    // Colour picked: let the theme finish its own update, then re-check this colour.
    const addon = target.closest?.(".shopify-section")?.querySelector(".js-lining-addon");
    if (addon) setTimeout(() => refresh(addon), 0);
  });

  init();
  window.addEventListener("load", init);
})();
